import { useState, useCallback } from 'react';
import { usePopup } from '../context/PopupContext';
import { Check, FileText, Search, ShieldCheck, ClipboardList, UserCheck, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';

// Step card data for Tab 1: Self Filing with Assistance
const selfFilingSteps = [
  {
    title: "Application Preparation",
    icon: FileText,
    description: "We prepare your trademark application on the USPTO TEAS system, ensuring accurate classification, proper formatting, and compliance with USPTO requirements."
  },
  {
    title: "Identity Verification & Review",
    icon: UserCheck,
    description: "You securely verify your identity via ID.me and review the prepared application by our IP attorney. You’ll have the opportunity to review all details and request any revisions before submission."
  },
  {
    title: "Filing",
    icon: ShieldCheck,
    description: "Once you review and approve the application, we will submit it on your behalf, ensuring all required details are accurately filed and in full compliance with official requirements of the USPTO."
  }
];

// Step card data for Tab 2: IP Attorney-Led Filing
const attorneyLedSteps = [
  {
    title: "Information Collection",
    icon: ClipboardList,
    description: "We collect your trademark details, including name, logo (if applicable), and a brief description of your products/services."
  },
  {
    title: "Attorney Review & Application Drafting",
    icon: FileText,
    description: "Our IP attorney reviews your case, determines the appropriate trademark class(es), and prepares a strong, USPTO-compliant application."
  },
  {
    title: "Filing & Case Management",
    icon: Briefcase,
    description: "We file the application on your behalf and handle all procedural steps, including monitoring your application and managing any USPTO correspondence."
  }
];

// Original pricing plans for Self Filing tab
const selfFilingPlans = [
  {
    name: "Basic",
    price: "$45",
    sub: "+ Federal Filing Fee",
    description: "Easy guided process where experts support your steps and ensure correctness",
    features: [
      "Case Review",
      "Case Filing",
      "Basic Search",
      "State E-Filing",
      "Trademark Secured",
      "Case Review",
      "Case Filing",
      "Basic Search"
    ],
    note: "Govt. filing fee not included",
    recommended: false,
    badge: null
  },
  {
    name: "Deluxe",
    price: "$145",
    sub: "+ Federal Filing Fee",
    description: "Balanced support system helping you move forward with clear professional guidance",
    features: [
      "Case Review",
      "Case Filing",
      "Direct Hit Search",
      "Trademark Alert",
      "Trademark Secured",
      "Case Review",
      "Case Filing",
      "Direct Hit Search"
    ],
    note: "Govt. filing fee not included",
    recommended: true,
    badge: "CORE OFFERING"
  },
  {
    name: "Premium",
    price: "$395",
    sub: "+ Federal Filing Fee",
    description: "Advanced assistance structure offering deeper help planning review and ongoing clarity",
    features: [
      "Case Review",
      "Case Filing",
      "Direct Hit Search",
      "Trademark Alert",
      "Trademark Secured",
      "Case Filing",
      "Case Review",
      "Case Filing",
      "Direct Hit Search",
      "Trademark Alert"
    ],
    note: "Govt. filing fee not included",
    recommended: false,
    badge: null
  },
  {
    name: "Imperial",
    price: "$595",
    sub: "+ Federal Filing Fee",
    description: "Premium guided experience with expert leadership attention and complete confidence",
    features: [
      "Case Review",
      "Case Filing",
      "Direct Hit Search",
      "Trademark Alert",
      "Trademark Secured",
      "Case Review",
      "Case Filing",
      "Direct Hit Search",
      "Trademark Alert",
      "Trademark Secured"
    ],
    note: "Govt. filing fee not included",
    recommended: false,
    badge: "FLAGSHIP SERVICE"
  }
];

// Pricing plans for Attorney-Led tab
const attorneyLedPlans = [
  {
    name: "Basic",
    price: "$795",
    sub: "+ Federal Filing Fee",
    description: "Simple reliable service guided by professionals for smooth easy completion process",
    features: [
      "Attorney-Handled Trademark Filing from Start to Finish",
      "Professional Case Evaluation",
      "Application Preparation & Submission",
      "Preliminary Trademark Search",
      "State-Level E-Filing Included",
      "Attorney-Handled Trademark Filing from Start to Finish",
      "Professional Case Evaluation",
      "Application Preparation & Submission",
      "Preliminary Trademark Search",
      "State-Level E-Filing Included"
    ],
    note: "Govt. filing fee not included",
    recommended: false,
    badge: null
  },
  {
    name: "Deluxe",
    price: "$895",
    sub: "+ Federal Filing Fee",
    description: "Better guided experience with added care support and improved overall confidence",
    features: [
      "Attorney-Led Trademark Filing",
      "End-to-End Case Handling",
      "Comprehensive Case Review & Analysis",
      "Professional Application Preparation",
      "Trademark Secured",
      "USPTO-Compliant Filing Process",
      "Attorney-Led Trademark Filing",
      "End-to-End Case Handling",
      "Comprehensive Case Review & Analysis"
    ],
    note: "Govt. filing fee not included",
    recommended: true,
    badge: "CORE OFFERING"
  },
  {
    name: "Premium",
    price: "$1,145",
    sub: "+ Federal Filing Fee",
    description: "Strong structured service with expert attention enhanced handling and steady progress",
    features: [
      "Attorney-Led Case Review",
      "Complete Case Filing & Management",
      "Advanced Direct-Hit Trademark Search",
      "Trademark Monitoring & Alerts",
      "Comprehensive Risk & Refusal Meter",
      "Attorney-Led Case Review",
      "Complete Case Filing & Management",
      "Advanced Direct-Hit Trademark Search",
      "Trademark Monitoring & Alerts",
      "Comprehensive Risk & Refusal Meter"
    ],
    note: "Govt. filing fee not included",
    recommended: false,
    badge: null
  },
  {
    name: "Imperial",
    price: "$1,545",
    sub: "+ Federal Filing Fee",
    description: "Premium priority service with highest focus care speed and complete assurance",
    features: [
      "Attorney-Led Case Review & Strategy",
      "Complete Case Filing & Management",
      "Advanced Direct-Hit Trademark Search",
      "Federal & State Trademark Search Report",
      "Trademark Monitoring & Real-Time Alerts",
      "Attorney-Led Case Review & Strategy",
      "Complete Case Filing & Management",
      "Advanced Direct-Hit Trademark Search",
      "Federal & State Trademark Search Report",
      "Trademark Monitoring & Real-Time Alerts"
    ],
    note: "Government filing fee included",
    recommended: false,
    badge: "FLAGSHIP SERVICE"
  }
];

// Step Card Component
function StepCard({ title, icon: Icon, description }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-6 hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300">
      <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
        <Icon size={22} className="text-[#D4AF37]" />
      </div>
      <h3 className="text-base font-bold text-gray-900 font-['Outfit'] mb-2">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

// Pricing Card Component (reusing existing design)
function PricingCard({ plan, onSelect }) {
  return (
    <div
      className={`relative rounded-sm border transition-all duration-300 hover:-translate-y-1 ${
        plan.recommended
          ? 'border-[#D4AF37] bg-white shadow-[0_0_30px_rgba(212,175,55,0.15)]'
          : 'border-gray-200 bg-white hover:border-[#D4AF37]/30'
      }`}
    >
      {plan.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className={`text-[#0A1128] text-[10px] font-bold px-4 py-1 rounded-full font-['Outfit'] tracking-wider uppercase ${
            plan.badge === 'CORE OFFERING' ? 'bg-[#D4AF37]' : 'bg-gray-300'
          }`}>
            {plan.badge}
          </span>
        </div>
      )}

      <div className="p-5 pb-3 border-b border-gray-200">
        <h3 className="text-base font-bold text-gray-900 font-['Outfit']">{plan.name}</h3>
        <div className="mt-2">
          <span className="text-2xl font-bold text-[#D4AF37] font-['Outfit']">{plan.price}</span>
          <p className="text-gray-600 text-xs mt-0.5">{plan.sub}</p>
        </div>
        {plan.description && (
          <p className="text-gray-500 text-xs mt-3 leading-relaxed">{plan.description}</p>
        )}
      </div>

      <div className="p-5">
        <ScrollArea className="h-[200px] w-full" type="auto">
          <ul className="space-y-2.5 pr-2">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <Check size={13} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 text-[13px] leading-snug">{feature}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-500 text-[11px] mt-3 italic">{plan.note}</p>
        </ScrollArea>
      </div>

      <div className="p-5 pt-0">
        <button
          onClick={() => onSelect(plan.name, parseFloat(plan.price.replace('$', '')))}
          className={`w-full font-bold py-2.5 rounded-sm text-sm transition-all duration-300 font-['Outfit'] tracking-wide ${
            plan.recommended
              ? 'bg-[#D4AF37] text-[#0A1128] hover:bg-[#F3E5AB] shadow-[0_4px_14px_0_rgba(212,175,55,0.39)]'
              : 'border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10'
          }`}
        >
          Get Started Now
        </button>
      </div>
    </div>
  );
}

// Tab Content Component
function TabContent({ steps, plans, title, subtitle, onSelect }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [stepDrag, setStepDrag] = useState({ isDragging: false, startX: 0, currentX: 0 });

  const totalSteps = steps.length;
  const maxStepIndex = totalSteps - 1;

  const stepNext = useCallback(() => {
    setStepIndex(prev => (prev >= maxStepIndex ? 0 : prev + 1));
  }, [maxStepIndex]);

  const stepPrev = useCallback(() => {
    setStepIndex(prev => (prev <= 0 ? maxStepIndex : prev - 1));
  }, [maxStepIndex]);

  const handleStepMouseDown = useCallback((e) => {
    setStepDrag({ isDragging: true, startX: e.clientX, currentX: 0 });
  }, []);

  const handleStepMouseMove = useCallback((e) => {
    if (!stepDrag.isDragging) return;
    const diff = e.clientX - stepDrag.startX;
    setStepDrag(prev => ({ ...prev, currentX: diff }));
  }, [stepDrag.isDragging, stepDrag.startX]);

  const handleStepMouseUp = useCallback(() => {
    if (!stepDrag.isDragging) return;
    const threshold = 50;
    if (stepDrag.currentX > threshold) {
      stepPrev();
    } else if (stepDrag.currentX < -threshold) {
      stepNext();
    }
    setStepDrag({ isDragging: false, startX: 0, currentX: 0 });
  }, [stepDrag.isDragging, stepDrag.currentX, stepPrev, stepNext]);

  const handleStepMouseLeave = useCallback(() => {
    if (stepDrag.isDragging) {
      setStepDrag({ isDragging: false, startX: 0, currentX: 0 });
    }
  }, [stepDrag.isDragging]);

  return (
    <div className="animate-fade-in">
      {/* Step Cards */}
      <div className="relative">
        {/* Mobile slider */}
        <div className="overflow-hidden md:hidden">
          <div 
            className="flex transition-transform duration-300 ease-in-out gap-4"
            style={{ transform: `translateX(-209%)` }}
            onMouseDown={handleStepMouseDown}
            onMouseMove={handleStepMouseMove}
            onMouseUp={handleStepMouseUp}
            onMouseLeave={handleStepMouseLeave}
          >
            {steps.map((step, index) => (
              <div key={index} className="min-w-full">
                <StepCard {...step} />
              </div>
            ))}
          </div>
        </div>

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-3 gap-6 mb-12">
          {steps.map((step, index) => (
            <StepCard key={index} {...step} />
          ))}
        </div>

        {/* Mobile navigation dots */}
        <div className="flex justify-center items-center mt-6 md:hidden mb-12">
          <div className="flex gap-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <button
                key={i}
                onClick={() => setStepIndex(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  stepIndex === i ? 'bg-[#D4AF37] w-6' : 'bg-[#1D2D50]'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section Heading */}
      <div className="text-center mb-10">
        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 font-['Outfit'] tracking-tight">
          {title}
        </h3>
        <p className="text-gray-600 mt-2 text-sm">{subtitle}</p>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans.map((plan) => (
          <PricingCard key={plan.name} plan={plan} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

export default function RevisedPricingSection() {
  const [activeTab, setActiveTab] = useState('self-filing');
  const { openPopup } = usePopup();

  const handleSelect = (name, price) => {
    // tmSignUpType: 1 for Self Filing, 2 for Attorney-Led
    const tmSignUpType = activeTab === 'self-filing' ? 1 : 2;
    openPopup(name, price, tmSignUpType);
  };

  return (
    <section data-testid="revised-pricing-section" className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-6 md:px-12">
        {/* Intro Section */}
        <div className="text-center mb-12">
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">Simple & Transparent Pricing Plans</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 font-['Outfit'] tracking-tight mb-6">
            Select the Right Protection for Your Brand
          </h2>
          <p className="text-gray-600 max-w-4xl mx-auto text-base leading-relaxed">
            Choose a trademark registration USA plan that matches your business needs. Every plan includes professional support, USPTO filing services, and step-by-step guidance to complete your trademark application and secure federal trademark protection.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-sm border border-gray-200 p-1">
            <button
              onClick={() => setActiveTab('self-filing')}
              className={`px-6 py-3 rounded-sm text-sm font-bold font-['Outfit'] tracking-wide transition-all duration-300 ${
                activeTab === 'self-filing'
                  ? 'bg-[#D4AF37] text-[#0A1128]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Self Filing with Assistance
            </button>
            <button
              onClick={() => setActiveTab('attorney-led')}
              className={`px-6 py-3 rounded-sm text-sm font-bold font-['Outfit'] tracking-wide transition-all duration-300 ${
                activeTab === 'attorney-led'
                  ? 'bg-[#D4AF37] text-[#0A1128]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              IP Attorney-Led Filing
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'self-filing' ? (
          <TabContent
            steps={selfFilingSteps}
            plans={selfFilingPlans}
            title="Self-Filing with Expert Assistance and Complete Support Plan for Easy Filing"
            subtitle="Complete Support Plan for Easy Filing"
            onSelect={handleSelect}
          />
        ) : (
          <TabContent
            steps={attorneyLedSteps}
            plans={attorneyLedPlans}
            title="Attorney Led Filing with Complete End-to-End Service Plan"
            subtitle="Complete End-to-End Service Plan"
            onSelect={handleSelect}
          />
        )}
      </div>
    </section>
  );
}
